import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api/api';
import { Users, CheckCircle } from 'lucide-react';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'var(--accent-purple-400)',
      admin: 'var(--accent-blue-400)',
      user: 'var(--accent-green-200)'
    };
    return colors[role] || colors.user;
  };

  const getPlanBadgeColor = (plan) => {
    const colors = {
      starter: 'var(--accent-blue-200)',
      professional: 'var(--accent-orange-200)',
      enterprise: 'var(--accent-purple-200)'
    };
    return colors[plan] || 'var(--bg-card)';
  };

  return (
    <DashboardLayout title="Admin Dashboard">
      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <div className="voice-card" style={{ background: 'var(--accent-blue-200)' }}>
          <div className="heading-2 mb-2">{users.length}</div>
          <div className="body-small">Total Users</div>
        </div>
        <div className="voice-card" style={{ background: 'var(--accent-green-200)' }}>
          <div className="heading-2 mb-2">{users.filter(u => u.is_active).length}</div>
          <div className="body-small">Active Users</div>
        </div>
        <div className="voice-card" style={{ background: 'var(--accent-orange-200)' }}>
          <div className="heading-2 mb-2">{users.filter(u => u.plan_type).length}</div>
          <div className="body-small">Paid Plans</div>
        </div>
        <div className="voice-card" style={{ background: 'var(--accent-purple-200)' }}>
          <div className="heading-2 mb-2">{users.filter(u => u.role === 'admin').length}</div>
          <div className="body-small">Admins</div>
        </div>
      </div>

      {/* Users List */}
      <div className="voice-card">
        <div className="flex items-center gap-2 mb-4">
          <Users size={24} />
          <h2 className="heading-3">All Users</h2>
        </div>
        
        {loading ? (
          <p>Loading...</p>
        ) : users.length === 0 ? (
          <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>No users yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--border-light)' }}>
                  <th className="text-left p-3 body-small font-semibold">Name</th>
                  <th className="text-left p-3 body-small font-semibold">Email</th>
                  <th className="text-left p-3 body-small font-semibold">Role</th>
                  <th className="text-left p-3 body-small font-semibold">Plan</th>
                  <th className="text-left p-3 body-small font-semibold">Status</th>
                  <th className="text-left p-3 body-small font-semibold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.user_id} className="border-b" style={{ borderColor: 'var(--border-light)' }}>
                    <td className="p-3">
                      <div className="font-semibold">{user.full_name}</div>
                    </td>
                    <td className="p-3">
                      <div className="body-small" style={{ color: 'var(--text-secondary)' }}>{user.email}</div>
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-1 rounded text-xs font-semibold capitalize" style={{ background: getRoleBadgeColor(user.role), color: 'white' }}>
                        {user.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-3">
                      {user.plan_type ? (
                        <span className="px-2 py-1 rounded text-xs capitalize" style={{ background: getPlanBadgeColor(user.plan_type) }}>
                          {user.plan_type}
                        </span>
                      ) : (
                        <span className="body-small" style={{ color: 'var(--text-muted)' }}>No plan</span>
                      )}
                    </td>
                    <td className="p-3">
                      {user.is_active ? (
                        <div className="flex items-center gap-1" style={{ color: 'var(--accent-green-200)' }}>
                          <CheckCircle size={16} />
                          <span className="text-xs">Active</span>
                        </div>
                      ) : (
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Inactive</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;